import React from 'react';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import StepNavigation from '@/components/medication/StepNavigation';
import { MedicationFormData, frequencyOptions } from '@/components/medication/types/medication';
import { cn } from '@/lib/utils';

interface ModalHeaderProps {
  currentStep: number;
  totalSteps: number;
  formData: MedicationFormData;
  showSummary?: boolean;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ 
  currentStep, 
  totalSteps, 
  formData,
  showSummary = true 
}) => {
  // Get the frequency label for display
  const frequencyLabel = formData.frequency ? 
    frequencyOptions.find(option => option.value === formData.frequency)?.label : 
    undefined;

  return (
    <div className="glass-header px-5 py-4 rounded-t-xl backdrop-blur-md bg-violet-50 dark:bg-violet-950/20 border-b border-border/30 h-full flex flex-col">
      <DialogHeader className="mb-2">
        <DialogTitle className="text-xl font-medium text-primary">
          Medication Setup
        </DialogTitle>
        <div className="text-xs text-muted-foreground">
          Simple step-by-step process
        </div>
      </DialogHeader>
      
      {/* Step indicator always appears at the top */}
      <div className="mb-4">
        <div className="flex flex-col gap-3">
          <div className={`flex items-center gap-3 ${currentStep === 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`flex items-center justify-center h-7 w-7 rounded-full ${currentStep === 1 ? 'bg-primary text-white' : 'border border-muted-foreground text-muted-foreground'}`}>
              1
            </div>
            <div>
              <div className={`text-sm font-medium ${currentStep === 1 ? 'text-primary' : 'text-muted-foreground'}`}>Details</div>
              <div className="text-xs">Basic medication information</div>
            </div>
          </div>
          
          <div className={`flex items-center gap-3 ${currentStep === 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`flex items-center justify-center h-7 w-7 rounded-full ${currentStep === 2 ? 'bg-primary text-white' : 'border border-muted-foreground text-muted-foreground'}`}>
              2
            </div>
            <div>
              <div className={`text-sm font-medium ${currentStep === 2 ? 'text-primary' : 'text-muted-foreground'}`}>Schedule</div>
              <div className="text-xs">When you need to take it</div>
            </div>
          </div>
          
          <div className={`flex items-center gap-3 ${currentStep === 3 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`flex items-center justify-center h-7 w-7 rounded-full ${currentStep === 3 ? 'bg-primary text-white' : 'border border-muted-foreground text-muted-foreground'}`}>
              3
            </div>
            <div>
              <div className={`text-sm font-medium ${currentStep === 3 ? 'text-primary' : 'text-muted-foreground'}`}>Cycle</div>
              <div className="text-xs">Start and end dates</div>
            </div>
          </div>
          
          <div className={`flex items-center gap-3 ${currentStep === 4 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`flex items-center justify-center h-7 w-7 rounded-full ${currentStep === 4 ? 'bg-primary text-white' : 'border border-muted-foreground text-muted-foreground'}`}>
              4
            </div>
            <div>
              <div className={`text-sm font-medium ${currentStep === 4 ? 'text-primary' : 'text-muted-foreground'}`}>Options</div>
              <div className="text-xs">Additional instructions</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Summary information appears below the step indicator */}
      {showSummary && formData.name && (
        <div className="flex-1 overflow-y-auto mt-2">
          <div className="space-y-1.5">
            <div className="text-sm font-medium py-1.5 px-3 bg-muted/20 rounded-md">
              {formData.name} {formData.dosage ? `(${formData.dosage})` : ''}
            </div>
            
            {formData.timeOfDay && formData.timeOfDay.length > 0 && (
              <div className="px-3 py-1.5 bg-muted/10 rounded-md">
                <div className="flex flex-wrap gap-1.5">
                  {frequencyLabel && (
                    <div className="bg-background/60 rounded-full py-0.5 px-2 text-xs flex items-center gap-1">
                      <span>Frequency: {frequencyLabel}</span>
                    </div>
                  )}
                  
                  {Object.entries(formData.dosesAtTime || {}).map(([timeKey, doseCount]) => {
                    const timeInfo = formData.timeOfDay.includes(timeKey);
                    if (!timeInfo) return null;
                    
                    return (
                      <div key={timeKey} className="bg-background/60 rounded-full py-0.5 px-2 text-xs flex items-center gap-1">
                        <span>{timeKey}: {doseCount} {doseCount === 1 ? 'dose' : 'doses'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {formData.hasCycle && (
              <div className="px-3 py-1.5 bg-muted/10 rounded-md">
                <div className="text-xs">
                  Cycle: {formData.cycleWeeksOn} {formData.cycleWeeksOn === 1 ? 'week' : 'weeks'} on, 
                  {formData.cycleWeeksOff} {formData.cycleWeeksOff === 1 ? 'week' : 'weeks'} off
                </div>
              </div>
            )}
            
            {formData.foodInstructions && (
              <div className="px-3 py-1.5 bg-muted/10 rounded-md">
                <div className="text-xs">
                  Take: {formData.foodInstructions === 'before-meals' ? 'Before meals' : 
                        formData.foodInstructions === 'with-meals' ? 'With meals' :
                        formData.foodInstructions === 'after-meals' ? 'After meals' : 'Any time'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalHeader;
